/**
 * Typed client for the HITL escalation review-queue endpoints
 * (ADR-0094 Slice 3):
 *
 *   GET  /v1/escalations?status=pending|approved|rejected|timed_out
 *        — owner/admin/reviewer list the queue.
 *   POST /v1/escalations/:id/resolve  body { decision: 'approve'|'reject' }
 *        — owner/admin resolve a held transaction. Approve settles the
 *          payment; reject cancels the order.
 *
 * Wire format is **snake_case** (CLAUDE.md §3); call-site code uses the
 * same field names — no camelCase conversion, mirroring every other
 * dashboard API client (`account-settlement-rail.ts`,
 * `audit-buyer-detail.ts`).
 *
 * Both functions collapse the HTTP outcomes into a discriminated union
 * so the queue view branches on `kind` without throw/catch boilerplate.
 * The resolve path carries the two load-bearing failure modes:
 *
 *   - 409 `conflict`               — another admin already resolved it.
 *   - 503 `settlement_enqueue_failed` — the queue push failed (retryable).
 *
 * The summary the list endpoint returns carries NO buyer PII by design;
 * buyer detail is fetched separately + access-logged via
 * `escalation-buyer-detail.ts`.
 */

import { AuthRateLimitError } from '@sill/types/api';

import type { UcpOrderBreakdown } from '../views/mandates/OrderMoneyBreakdown.js';
import { ApiError, type HttpClient } from './http-client.js';

/** Lifecycle status of a held escalation. */
export type EscalationStatus = 'pending' | 'approved' | 'rejected' | 'timed_out';

/** The status filter a queue tab requests. `all` omits the query param. */
export type EscalationStatusFilter = 'all' | EscalationStatus;

/** Resolve decision the operator sends. */
export type EscalationDecision = 'approve' | 'reject';

/**
 * The signed decision class the server echoes on a successful resolve.
 * Mirrors the `AuditDecision` escalation values in `@sill/types`.
 */
export type EscalationResolvedDecision = 'escalated_approved' | 'escalated_rejected';

/**
 * Sanitized order summary on each escalation row. Carries NO buyer PII
 * by design — buyer detail is a separate, access-logged decrypt. Every
 * field beyond `action` + `merchant` is optional because the held
 * mandate may not carry a full cart (e.g. a refund escalation).
 */
export interface EscalationSummary {
  readonly action: string;
  readonly merchant: string;
  /** Protocol the order arrived on (`ap2`, `ucp`, `mcp`, …), when known. */
  readonly protocol?: string;
  readonly sku?: string;
  /**
   * Multi-SKU cart lines (ADR-0077). Present only when the held mandate
   * carried more than the single scalar `sku` — each entry is sanitized
   * server-side to `{ sku, quantity }` (quantity defaults to 1). The
   * scalar `sku` stays populated for single-SKU back-comp.
   *
   * ADR-0253 2026-10-27 — AP2 CartMandates + UCP checkouts set an
   * OPTIONAL merchant-signed `name` per line (same bounded shape as
   * the audit-record `intent_summary.line_items[i].name`); the
   * EscalationDetail / EscalationList row prefers it over the
   * catalogue-sourced `product_titles` lookup when present.
   */
  readonly line_items?: readonly { readonly sku: string; readonly quantity: number; readonly name?: string }[];
  /**
   * Display names keyed by SKU, resolved server-side from the site's synced
   * catalog (the held intent carries only SKU + quantity). Absent when no
   * SKU matched; an unmatched SKU renders bare.
   */
  readonly product_titles?: Readonly<Record<string, string>>;
  readonly max_amount?: number;
  readonly quoted_total?: number;
  readonly currency?: string;
  readonly failed_rule_id?: string;
  /**
   * Refund triplet (ADR-0099) — present only when `action ===
   * 'request_refund'`. The refund mandate's intent is a CLOSED shape
   * (no order details, anti-amount-smuggling); these are the non-PII
   * identifiers/enums the operator decides on. `max_amount` is the
   * agent-signed CAP, not the refund amount — label it accordingly.
   */
  readonly original_mandate_id?: string;
  readonly reason_code?: string;
  readonly scope?: 'full' | 'partial';
}

/**
 * One per-line entry of the original order's settled evidence
 * (`shopify_order_state.line_items_evidence`, ADR-0078). Merchant
 * catalogue data only — never buyer-identifying.
 */
export interface OriginalOrderLineItem {
  readonly sku?: string;
  readonly quantity?: number;
  readonly variant_id?: string;
  readonly product_title?: string;
  readonly variant_price_minor?: number;
  readonly line_total_minor?: number;
}

/**
 * Settled Shopify evidence for the ORIGINAL order a refund escalation
 * targets. All `*_minor` fields are integer minor units (cents);
 * `currency` is upper-case ISO-4217 (the shopify_order_state CHECK).
 */
export interface OriginalOrderShopify {
  readonly shopify_order_id?: string;
  /** Task #14 — Shopify's human-readable order name (e.g. "#1001"),
   *  rendered as the Shopify order link text in place of the raw GID.
   *  NON-PII; absent on orders that pre-date the field. */
  readonly order_name?: string;
  /** The merchant's `*.myshopify.com` domain — used with `shopify_order_id`
   *  to deep-link the order in Shopify Admin. NON-PII. */
  readonly shop_domain?: string;
  readonly state: string;
  readonly financial_status_at_create?: string;
  readonly subtotal_minor?: number;
  readonly tax_minor?: number;
  readonly shipping_minor?: number;
  readonly total_minor?: number;
  readonly currency?: string;
  readonly product_title?: string;
  readonly variant_id?: string;
  readonly line_items_evidence?: readonly OriginalOrderLineItem[];
}

/**
 * Settled STRIPE evidence for the ORIGINAL order a refund targets — the
 * Stripe analogue of {@link OriginalOrderShopify}. NON-PII. `currency` is
 * upper-case ISO-4217; `stripe_mode` selects the `/test/` dashboard prefix.
 */
export interface OriginalOrderStripe {
  readonly stripe_charge_id?: string;
  readonly state: string;
  readonly stripe_mode: 'test' | 'live';
  readonly total_minor?: number;
  readonly currency?: string;
}

/**
 * The original order's intent as originally approved — the server's
 * non-PII allow-list projection of the original audit row's
 * `intent_summary`. All fields optional (the projection emits only what
 * the stored intent carried); NEVER a buyer block.
 */
export interface OriginalOrderIntent {
  readonly action?: string;
  readonly merchant?: string;
  readonly sku?: string;
  readonly line_items?: readonly { readonly sku: string; readonly quantity: number }[];
  readonly max_amount?: number;
  readonly quoted_total?: number;
  readonly currency?: string;
}

/**
 * Server-resolved enrichment for a REFUND escalation (ADR-0099 follow-up).
 * The refund mandate deliberately carries NO order details, so the server
 * resolves the original mandate's audit row + Shopify order state —
 * site-bound to the escalation's own site — and ships the reviewable
 * substance here. Carries NO buyer PII: `original_record_id` feeds the
 * EXISTING access-logged reveal (`GET /v1/audit/:record_id/buyer-detail`).
 */
export interface EscalationOriginalOrder {
  readonly original_record_id: string;
  /** The original place_order mandate id (`mnd_…`) — non-PII; shown so
   *  the reviewer/auditor sees the same id the resolution card carries. */
  readonly original_mandate_id?: string;
  /** The original decision — 'approved' | 'escalated_approved'. */
  readonly decision: string;
  readonly evaluated_at: string;
  /** Products/sku/quantity as originally approved (non-PII projection). */
  readonly intent_summary?: OriginalOrderIntent;
  /** Null when the original settled on a non-Shopify rail. */
  readonly shopify_order: OriginalOrderShopify | null;
  /** Null when the original settled on a non-Stripe rail (or pre-dates
   *  charge_state). The panel renders whichever of shopify_order /
   *  stripe_charge is non-null. Optional for back-compat with records
   *  resolved before this field shipped. */
  readonly stripe_charge?: OriginalOrderStripe | null;
}

/**
 * One escalation row. `resolved_at` is present only once the row leaves
 * `pending`; `timeout_at` is the deadline after which an unreviewed row
 * auto-rejects (`timed_out`).
 *
 * Shape MUST agree with the origin `EscalationRow` declared server-side.
 */
export interface EscalationItem {
  readonly escalation_id: string;
  readonly mandate_id: string;
  readonly rule_id: string;
  readonly status: EscalationStatus;
  readonly created_at: string;
  readonly timeout_at: string;
  readonly resolved_at?: string;
  /**
   * Raw site ULID the held order belongs to. The server always sends it
   * now (the queue is per-site scoped), but it is parsed defensively as
   * optional so older callers/fixtures stay valid. The dashboard maps it
   * to `activeSite.domain` for DISPLAY — the wire never carries the
   * domain (the queue stays non-PII / minimal).
   */
  readonly site_id?: string;
  /**
   * The linked audit record (server LEFT JOIN on
   * `audit_record.escalation_id`). Present once a resolution settled —
   * the detail panel fetches `GET /v1/audit/:record_id` with it and
   * renders the settled money breakdown (line-item names, tax, shipping,
   * total) exactly as the Mandates detail does. Absent while pending.
   */
  readonly audit_record_id?: string;
  readonly summary: EscalationSummary;
  /**
   * Refund escalations only (ADR-0099): the server-resolved original
   * order. `null` means the original could not be resolved on this site
   * — the detail panel renders a refuse-this-refund fallback note.
   * Absent entirely on non-refund escalations.
   */
  readonly original_order?: EscalationOriginalOrder | null;
  /**
   * UCP holds only: the full money breakdown (per-line price, shipping,
   * tax, total) resolved from the checkout session the held mandate
   * came from. The held intent carries only SKU + quantity + the signed
   * `max_amount` cap — the checkout session is where the per-line price
   * and the computed shipping/tax live. Display-only; UNSIGNED. Absent
   * on holds that did not originate at a UCP checkout (Stripe-direct
   * place_order mandates, refund holds, Shopify-rail holds).
   */
  readonly breakdown?: UcpOrderBreakdown;
  /**
   * ops2026 UCP checkout test (2026-10-21). TRUE when this held order
   * belongs to a staff-run test checkout — the server attaches it via a
   * batched lookup against `ucp_checkout_session.escalation_id`. The
   * Held orders list row + detail header render a neutral "SILL TEST"
   * pill so a merchant can tell a Sill-run test apart from a real-looking
   * order. Omitted (undefined) when FALSE.
   */
  readonly staff_test?: boolean;
}

/**
 * Result of `listEscalations` — discriminated union the view branches on.
 *   - `ok`        — server returned 200 with the items array.
 *   - `forbidden` — caller's role is viewer (server 403).
 *   - `error`     — network / 5xx / bad shape.
 */
export type ListEscalationsResult =
  | { readonly kind: 'ok'; readonly items: readonly EscalationItem[] }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * Result of `resolveEscalation` — discriminated union.
 *   - `ok`                — server accepted; echoes new status + decision.
 *   - `conflict`          — 409, already resolved by another admin. Carries
 *                           the server's current `status` so the view can
 *                           reconcile + refetch.
 *   - `enqueue_failed`    — 503 `settlement_enqueue_failed`; retryable.
 *   - `forbidden`         — role gate (server 403).
 *   - `not_found`         — 404, escalation missing / RLS-filtered.
 *   - `error`             — network / 5xx / bad shape. `network` flag
 *                           distinguishes `offline` (no answer, retryable)
 *                           from `timeout` (we gave up waiting; the server
 *                           may have completed). The approve view uses
 *                           this to drive a silent refetch so the list
 *                           reflects reality after the toast.
 */
export type ResolveEscalationResult =
  | {
      readonly kind: 'ok';
      readonly escalation_id: string;
      readonly status: 'approved' | 'rejected';
      readonly decision: EscalationResolvedDecision;
    }
  | { readonly kind: 'conflict'; readonly status?: EscalationStatus }
  | { readonly kind: 'enqueue_failed' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | {
      readonly kind: 'error';
      readonly message: string;
      readonly network?: 'offline' | 'timeout';
    };

/* ---------- internal wire shapes (snake_case from the server) ---------- */

interface ListWire {
  readonly items?: readonly unknown[];
}

interface ResolveWire {
  readonly escalation_id: string;
  readonly status: string;
  readonly decision: string;
}

interface ConflictWire {
  readonly error?: string;
  readonly status?: string;
}

/* --------------------------------------------------------------------- */

/**
 * GET /v1/escalations?status=…&site_id=…
 *
 * `status === 'all'` omits the status param (the server returns the full
 * queue regardless of status). Any other value is passed through verbatim.
 *
 * `site_id` is OPTIONAL: when provided (non-empty) it scopes the queue to a
 * single site (mirrors the Mandates list, which scopes to the active site).
 * When omitted the server returns the account-wide queue — byte-for-byte
 * the prior behavior, so existing callers/tests keep working.
 */
export async function listEscalations(
  http: HttpClient,
  status: EscalationStatusFilter = 'all',
  site_id?: string,
): Promise<ListEscalationsResult> {
  const params = new URLSearchParams();
  if (status !== 'all') params.set('status', status);
  if (site_id !== undefined && site_id.length > 0) params.set('site_id', site_id);
  const qs = params.toString();
  const query = qs.length > 0 ? `?${qs}` : '';
  try {
    const res = await http.request<ListWire>({
      method: 'GET',
      path: `/v1/escalations${query}`,
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const raw = Array.isArray(res.items) ? res.items : [];
    const items: EscalationItem[] = [];
    for (const row of raw) {
      const parsed = parseEscalationItem(row);
      if (parsed !== null) items.push(parsed);
    }
    return { kind: 'ok', items };
  } catch (err) {
    return mapListError(err);
  }
}

/**
 * POST /v1/escalations/:id/resolve  body { decision }.
 *
 * Owner/admin only — viewer + reviewer are blocked at the call site
 * (the view hides the action buttons) AND server-side (403). The two
 * load-bearing recoverable failures (409 conflict, 503 enqueue-failed)
 * are surfaced as their own union members so the view can drive the
 * refetch / retry UX.
 */
export async function resolveEscalation(
  http: HttpClient,
  escalation_id: string,
  decision: EscalationDecision,
): Promise<ResolveEscalationResult> {
  try {
    const res = await http.request<ResolveWire>({
      method: 'POST',
      path: `/v1/escalations/${encodeURIComponent(escalation_id)}/resolve`,
      body: { decision },
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const status = res.status === 'approved' || res.status === 'rejected'
      ? res.status
      : null;
    if (status === null) {
      return { kind: 'error', message: 'Server returned an unexpected resolve shape.' };
    }
    // `decision` is present on a FRESH resolve; the idempotent already-resolved
    // 200 omits it (origin returns just { escalation_id, status }) when another
    // admin already resolved it the SAME way. Derive the decision from the
    // status so that case reads as success, not a dead-end error toast.
    const resolved: EscalationResolvedDecision =
      res.decision === 'escalated_approved' || res.decision === 'escalated_rejected'
        ? res.decision
        : status === 'approved'
          ? 'escalated_approved'
          : 'escalated_rejected';
    return { kind: 'ok', escalation_id: res.escalation_id, status, decision: resolved };
  } catch (err) {
    return await mapResolveError(err);
  }
}

/* ---------- pre-flight quote (ADR-0094 follow-up) ---------- */

/**
 * Projected settlement for a PENDING escalation — Shopify-computed tax +
 * shipping via the same calculate the rail runs at settlement (zero
 * mutations server-side). `within_ceiling: false` means an approval
 * would FAIL the pre-create ceiling check (`amount_exceeds_mandate`).
 * All money fields are integer minor units (cents).
 */
export interface EscalationQuote {
  readonly subtotal_minor: number;
  readonly tax_minor: number;
  readonly shipping_minor: number;
  readonly discount_minor: number;
  readonly total_minor: number;
  readonly ceiling_minor: number;
  readonly within_ceiling: boolean;
  readonly currency: string;
  readonly chosen_rate_title: string | null;
  readonly line_items: readonly {
    readonly sku: string;
    readonly quantity: number;
    readonly product_title: string;
    readonly line_total_minor: number;
  }[];
}

export type EscalationQuoteResult =
  | { readonly kind: 'ok'; readonly quote: EscalationQuote }
  | { readonly kind: 'unavailable'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

interface QuoteWire {
  readonly quote?: unknown;
  readonly unavailable?: unknown;
  readonly reason?: unknown;
}

/**
 * GET /v1/escalations/:id/preflight-quote — owner/admin/reviewer.
 * Rate-limited server-side under the shared buyer-detail decrypt budget
 * (429 surfaces as `error`). `unavailable` is the SOFT no-quote outcome
 * (non-Shopify rail, already resolved, out-of-stock cart, …) — the view
 * degrades to the held summary.
 */
export async function fetchEscalationPreflightQuote(
  http: HttpClient,
  escalation_id: string,
): Promise<EscalationQuoteResult> {
  try {
    const res = await http.request<QuoteWire>({
      method: 'GET',
      path: `/v1/escalations/${encodeURIComponent(escalation_id)}/preflight-quote`,
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    if (res.unavailable === true) {
      return {
        kind: 'unavailable',
        reason: typeof res.reason === 'string' ? res.reason : 'unknown',
      };
    }
    const quote = parseQuote(res.quote);
    if (quote === null) {
      return { kind: 'error', message: 'Server returned an unexpected quote shape.' };
    }
    return { kind: 'ok', quote };
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'error', message: err.message };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

function parseQuote(raw: unknown): EscalationQuote | null {
  if (raw === null || typeof raw !== 'object') return null;
  const q = raw as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;
  const subtotal_minor = num(q['subtotal_minor']);
  const tax_minor = num(q['tax_minor']);
  const shipping_minor = num(q['shipping_minor']);
  const discount_minor = num(q['discount_minor']) ?? 0;
  const total_minor = num(q['total_minor']);
  const ceiling_minor = num(q['ceiling_minor']);
  if (
    subtotal_minor === null
    || tax_minor === null
    || shipping_minor === null
    || total_minor === null
    || ceiling_minor === null
    || typeof q['within_ceiling'] !== 'boolean'
  ) {
    return null;
  }
  const line_items: EscalationQuote['line_items'] = Array.isArray(q['line_items'])
    ? (q['line_items'] as unknown[]).flatMap((entry) => {
        if (entry === null || typeof entry !== 'object') return [];
        const e = entry as Record<string, unknown>;
        if (typeof e['sku'] !== 'string' || typeof e['product_title'] !== 'string') return [];
        const lt = num(e['line_total_minor']);
        const qty = num(e['quantity']);
        if (lt === null || qty === null) return [];
        return [{
          sku: e['sku'],
          quantity: qty,
          product_title: e['product_title'],
          line_total_minor: lt,
        }];
      })
    : [];
  return {
    subtotal_minor,
    tax_minor,
    shipping_minor,
    discount_minor,
    total_minor,
    ceiling_minor,
    within_ceiling: q['within_ceiling'],
    currency: typeof q['currency'] === 'string' ? q['currency'] : 'USD',
    chosen_rate_title:
      typeof q['chosen_rate_title'] === 'string' ? q['chosen_rate_title'] : null,
    line_items,
  };
}

/* ---------- error mapping ---------- */

function mapListError(err: unknown): ListEscalationsResult {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return { kind: 'forbidden' };
    return { kind: 'error', message: explainApiError(err) };
  }
  if (err instanceof AuthRateLimitError) {
    return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
  }
  // AuthUnauthorizedError falls through — the http-client already invoked
  // on_unauthorized (route to login). Surface a generic message so the
  // view doesn't hang.
  return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
}

async function mapResolveError(err: unknown): Promise<ResolveEscalationResult> {
  if (err instanceof ApiError) {
    if (err.kind === 'conflict') {
      // The 409 body carries the server's current `status` so the view
      // can reconcile its optimistic update. The http-client only parses
      // a top-level `reason`; the conflict status sits under `status`,
      // so it is not surfaced through `server_reason`. Treat it as
      // best-effort: an absent status still drives the refetch.
      const status = parseStatus(err.server_reason);
      return status !== null ? { kind: 'conflict', status } : { kind: 'conflict' };
    }
    // ANY 503 on the resolve path is a RETRYABLE settlement failure — the
    // route only 503s for `settlement_enqueue_failed` (queue push) or
    // `settlement_unavailable` (producer unwired), both of which leave the
    // escalation re-resolvable. The discriminant rides under `error` (not
    // `reason`), so `server_reason` is undefined here — gate on the status
    // class, not the sub-reason, or the retry UX is dead code.
    if (err.kind === 'service_unavailable') {
      return { kind: 'enqueue_failed' };
    }
    if (err.kind === 'forbidden') return { kind: 'forbidden' };
    if (err.kind === 'not_found') return { kind: 'not_found' };
    if (err.kind === 'network_timeout') {
      return {
        kind: 'error',
        message: explainApiError(err),
        network: 'timeout',
      };
    }
    if (err.kind === 'network_error') {
      return {
        kind: 'error',
        message: explainApiError(err),
        network: 'offline',
      };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
  if (err instanceof AuthRateLimitError) {
    return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
  }
  return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
}

/* ---------- parsing helpers ---------- */

function parseStatus(raw: string | undefined): EscalationStatus | null {
  switch (raw) {
    case 'pending':
    case 'approved':
    case 'rejected':
    case 'timed_out':
      return raw;
    default:
      return null;
  }
}

/**
 * Validate + narrow one wire row into an `EscalationItem`. Returns null
 * on a malformed row so a single bad entry can't poison the whole list
 * render (the view skips it rather than throwing).
 */
function parseEscalationItem(row: unknown): EscalationItem | null {
  if (row === null || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const escalation_id = r['escalation_id'];
  const mandate_id = r['mandate_id'];
  const rule_id = r['rule_id'];
  const created_at = r['created_at'];
  const timeout_at = r['timeout_at'];
  const status = parseStatus(typeof r['status'] === 'string' ? (r['status'] as string) : undefined);
  if (
    typeof escalation_id !== 'string'
    || typeof mandate_id !== 'string'
    || typeof rule_id !== 'string'
    || typeof created_at !== 'string'
    || typeof timeout_at !== 'string'
    || status === null
  ) {
    return null;
  }
  const summary = parseSummary(r['summary']);
  if (summary === null) return null;
  const resolved_at = typeof r['resolved_at'] === 'string' ? (r['resolved_at'] as string) : undefined;
  const site_id =
    typeof r['site_id'] === 'string' && (r['site_id'] as string).length > 0
      ? (r['site_id'] as string)
      : undefined;
  const audit_record_id =
    typeof r['audit_record_id'] === 'string' && (r['audit_record_id'] as string).length > 0
      ? (r['audit_record_id'] as string)
      : undefined;
  // Refund-only enrichment: explicit null (unresolvable original) is
  // distinct from absent (non-refund) — both survive the parse verbatim.
  const original_order =
    'original_order' in r ? parseOriginalOrder(r['original_order']) : undefined;
  // UCP holds only: order money breakdown resolved from the checkout
  // session (price, shipping, tax, total). Parsed defensively — a
  // malformed field drops silently so the queue still renders.
  const breakdown = parseUcpBreakdown(r['breakdown']);
  // ops2026 UCP checkout test (2026-10-21) — the server emits `true` when
  // the held order belongs to a staff-run test; omitted otherwise.
  const staff_test = r['staff_test'] === true ? true : undefined;
  return {
    escalation_id,
    mandate_id,
    rule_id,
    status,
    created_at,
    timeout_at,
    summary,
    ...(resolved_at !== undefined ? { resolved_at } : {}),
    ...(site_id !== undefined ? { site_id } : {}),
    ...(audit_record_id !== undefined ? { audit_record_id } : {}),
    ...(original_order !== undefined ? { original_order } : {}),
    ...(breakdown !== undefined ? { breakdown } : {}),
    ...(staff_test !== undefined ? { staff_test } : {}),
  };
}

/**
 * Parse the UCP checkout breakdown block on a held escalation. Mirrors
 * the shape in `services/api/src/ucp/order-breakdown.ts`. Permissive on
 * optional fields; strict on the five load-bearing ints (subtotal,
 * total, discount, every line's unit/line total). Returns `undefined`
 * on any shape drift so the list still renders without the breakdown.
 */
function parseUcpBreakdown(raw: unknown): UcpOrderBreakdown | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const b = raw as Record<string, unknown>;
  if (b['source'] !== 'ucp_checkout') return undefined;
  const currency = b['currency'];
  if (typeof currency !== 'string' || currency.length === 0) return undefined;
  const subtotal_minor = b['subtotal_minor'];
  const discount_minor = b['discount_minor'];
  const total_minor = b['total_minor'];
  const isMinor = (v: unknown): v is number =>
    typeof v === 'number' && Number.isInteger(v) && v >= 0;
  if (!isMinor(subtotal_minor) || !isMinor(discount_minor) || !isMinor(total_minor)) {
    return undefined;
  }
  if (!Array.isArray(b['line_items'])) return undefined;
  const line_items: UcpOrderBreakdown['line_items'][number][] = [];
  for (const entry of b['line_items']) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const li = entry as Record<string, unknown>;
    const sku = li['sku'];
    const title = li['title'];
    const quantity = li['quantity'];
    const unit_price_minor = li['unit_price_minor'];
    const line_total_minor = li['line_total_minor'];
    if (
      typeof sku !== 'string'
      || sku.length === 0
      || typeof title !== 'string'
      || typeof quantity !== 'number'
      || !Number.isInteger(quantity)
      || quantity <= 0
      || !isMinor(unit_price_minor)
      || !isMinor(line_total_minor)
    ) {
      continue;
    }
    line_items.push({ sku, title, quantity, unit_price_minor, line_total_minor });
  }
  if (line_items.length === 0) return undefined;
  const shipping_raw = b['shipping_minor'];
  const shipping_minor =
    shipping_raw === null ? null : isMinor(shipping_raw) ? shipping_raw : null;
  const tax_raw = b['tax_minor'];
  const tax_minor = tax_raw === null ? null : isMinor(tax_raw) ? tax_raw : null;
  const shipping_title_raw = b['shipping_title'];
  const shipping_title =
    typeof shipping_title_raw === 'string' && shipping_title_raw.length > 0
      ? shipping_title_raw
      : null;
  const tax_label_raw = b['tax_label'];
  const tax_label =
    typeof tax_label_raw === 'string' && tax_label_raw.length > 0 ? tax_label_raw : null;
  // Optional on-wire — older rows pre-date these fields. We parse them
  // defensively so a malformed shape still renders the money breakdown;
  // callers that need `ship_to` / `platform_host` branch on `undefined`.
  let ship_to: { country: string; region: string | null } | null | undefined;
  const ship_to_raw = b['ship_to'];
  if (ship_to_raw === null) {
    ship_to = null;
  } else if (ship_to_raw !== undefined && typeof ship_to_raw === 'object' && !Array.isArray(ship_to_raw)) {
    const st = ship_to_raw as Record<string, unknown>;
    const country = st['country'];
    const region = st['region'];
    if (typeof country === 'string' && /^[A-Za-z]{2}$/.test(country)) {
      ship_to = {
        country: country.toUpperCase(),
        region: typeof region === 'string' && region.length > 0 ? region : null,
      };
    }
  }
  const platform_host_raw = b['platform_host'];
  const platform_host =
    platform_host_raw === null
      ? null
      : typeof platform_host_raw === 'string' && platform_host_raw.length > 0
        ? platform_host_raw
        : undefined;
  return {
    source: 'ucp_checkout',
    currency,
    line_items,
    subtotal_minor,
    discount_minor,
    ...(typeof b['discount_code'] === 'string' && /^[A-Z0-9_-]{1,64}$/.test(b['discount_code']) ? { discount_code: b['discount_code'] } : {}),
    shipping_minor,
    shipping_title,
    tax_minor,
    tax_label,
    total_minor,
    ...(ship_to !== undefined ? { ship_to } : {}),
    ...(platform_host !== undefined ? { platform_host } : {}),
    ...(b['platform_auth'] === 'signature' || b['platform_auth'] === 'api_key' ? { platform_auth: b['platform_auth'] } : {}),
  };
}

function parseSummary(raw: unknown): EscalationSummary | null {
  if (raw === null || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  const action = s['action'];
  const merchant = s['merchant'];
  if (typeof action !== 'string' || typeof merchant !== 'string') return null;
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const sku = str(s['sku']);
  const currency = str(s['currency']);
  const failed_rule_id = str(s['failed_rule_id']);
  const max_amount = num(s['max_amount']);
  const quoted_total = num(s['quoted_total']);
  const line_items = parseLineItems(s['line_items']);
  const product_titles = parseProductTitles(s['product_titles']);
  // Refund triplet (ADR-0099) — server emits these only for refunds.
  const original_mandate_id = str(s['original_mandate_id']);
  const reason_code = str(s['reason_code']);
  const scope = s['scope'] === 'full' || s['scope'] === 'partial' ? s['scope'] : undefined;
  const protocol = str(s['protocol']);
  return {
    action,
    merchant,
    ...(sku !== undefined ? { sku } : {}),
    ...(line_items !== undefined ? { line_items } : {}),
    ...(product_titles !== undefined ? { product_titles } : {}),
    ...(currency !== undefined ? { currency } : {}),
    ...(failed_rule_id !== undefined ? { failed_rule_id } : {}),
    ...(max_amount !== undefined ? { max_amount } : {}),
    ...(quoted_total !== undefined ? { quoted_total } : {}),
    ...(original_mandate_id !== undefined ? { original_mandate_id } : {}),
    ...(reason_code !== undefined ? { reason_code } : {}),
    ...(scope !== undefined ? { scope } : {}),
    ...(protocol !== undefined && /^[a-z0-9]{2,8}$/.test(protocol) ? { protocol } : {}),
  };
}

/**
 * Parse the refund original-order enrichment. Returns:
 *   - `undefined` — malformed (treated as absent; the panel renders the
 *     unresolved fallback for refunds),
 *   - `null`      — server says the original could not be resolved,
 *   - the parsed block otherwise. Permissive on optional fields; strict
 *     on the load-bearing `original_record_id`.
 */
function parseOriginalOrder(raw: unknown): EscalationOriginalOrder | null | undefined {
  if (raw === null) return null;
  if (typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  const original_record_id = o['original_record_id'];
  const decision = o['decision'];
  const evaluated_at = o['evaluated_at'];
  if (
    typeof original_record_id !== 'string'
    || original_record_id.length === 0
    || typeof decision !== 'string'
    || typeof evaluated_at !== 'string'
  ) {
    return undefined;
  }
  const intent_summary = parseOriginalIntent(o['intent_summary']);
  const shopify_order = parseOriginalShopify(o['shopify_order']);
  const stripe_charge = parseOriginalStripe(o['stripe_charge']);
  const original_mandate_id = o['original_mandate_id'];
  return {
    original_record_id,
    ...(typeof original_mandate_id === 'string' && original_mandate_id.length > 0
      ? { original_mandate_id }
      : {}),
    decision,
    evaluated_at,
    ...(intent_summary !== undefined ? { intent_summary } : {}),
    shopify_order,
    stripe_charge,
  };
}

function parseOriginalStripe(raw: unknown): OriginalOrderStripe | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const s = raw as Record<string, unknown>;
  const state = s['state'];
  if (typeof state !== 'string') return null;
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const stripe_mode = s['stripe_mode'] === 'live' ? 'live' : 'test';
  return {
    state,
    stripe_mode,
    ...(str(s['stripe_charge_id']) !== undefined
      ? { stripe_charge_id: s['stripe_charge_id'] as string }
      : {}),
    ...(num(s['total_minor']) !== undefined ? { total_minor: s['total_minor'] as number } : {}),
    ...(str(s['currency']) !== undefined ? { currency: s['currency'] as string } : {}),
  };
}

function parseOriginalIntent(raw: unknown): OriginalOrderIntent | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const s = raw as Record<string, unknown>;
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const action = str(s['action']);
  const merchant = str(s['merchant']);
  const sku = str(s['sku']);
  const currency = str(s['currency']);
  const max_amount = num(s['max_amount']);
  const quoted_total = num(s['quoted_total']);
  const line_items = parseLineItems(s['line_items']);
  const out: OriginalOrderIntent = {
    ...(action !== undefined ? { action } : {}),
    ...(merchant !== undefined ? { merchant } : {}),
    ...(sku !== undefined ? { sku } : {}),
    ...(line_items !== undefined ? { line_items } : {}),
    ...(currency !== undefined ? { currency } : {}),
    ...(max_amount !== undefined ? { max_amount } : {}),
    ...(quoted_total !== undefined ? { quoted_total } : {}),
  };
  return Object.keys(out).length > 0 ? out : undefined;
}

function parseOriginalShopify(raw: unknown): OriginalOrderShopify | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const s = raw as Record<string, unknown>;
  const state = s['state'];
  if (typeof state !== 'string') return null;
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const line_items_evidence = Array.isArray(s['line_items_evidence'])
    ? (s['line_items_evidence'] as unknown[]).flatMap((entry): OriginalOrderLineItem[] => {
        if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return [];
        const e = entry as Record<string, unknown>;
        const line: OriginalOrderLineItem = {
          ...(str(e['sku']) !== undefined ? { sku: e['sku'] as string } : {}),
          ...(num(e['quantity']) !== undefined ? { quantity: e['quantity'] as number } : {}),
          ...(str(e['variant_id']) !== undefined ? { variant_id: e['variant_id'] as string } : {}),
          ...(str(e['product_title']) !== undefined
            ? { product_title: e['product_title'] as string }
            : {}),
          ...(num(e['variant_price_minor']) !== undefined
            ? { variant_price_minor: e['variant_price_minor'] as number }
            : {}),
          ...(num(e['line_total_minor']) !== undefined
            ? { line_total_minor: e['line_total_minor'] as number }
            : {}),
        };
        return Object.keys(line).length > 0 ? [line] : [];
      })
    : undefined;
  return {
    state,
    ...(str(s['shopify_order_id']) !== undefined
      ? { shopify_order_id: s['shopify_order_id'] as string }
      : {}),
    // Task #14 — human-readable order name (string only).
    ...(str(s['order_name']) !== undefined
      ? { order_name: s['order_name'] as string }
      : {}),
    ...(str(s['shop_domain']) !== undefined
      ? { shop_domain: s['shop_domain'] as string }
      : {}),
    ...(str(s['financial_status_at_create']) !== undefined
      ? { financial_status_at_create: s['financial_status_at_create'] as string }
      : {}),
    ...(num(s['subtotal_minor']) !== undefined ? { subtotal_minor: s['subtotal_minor'] as number } : {}),
    ...(num(s['tax_minor']) !== undefined ? { tax_minor: s['tax_minor'] as number } : {}),
    ...(num(s['shipping_minor']) !== undefined
      ? { shipping_minor: s['shipping_minor'] as number }
      : {}),
    ...(num(s['total_minor']) !== undefined ? { total_minor: s['total_minor'] as number } : {}),
    ...(str(s['currency']) !== undefined ? { currency: s['currency'] as string } : {}),
    ...(str(s['product_title']) !== undefined
      ? { product_title: s['product_title'] as string }
      : {}),
    ...(str(s['variant_id']) !== undefined ? { variant_id: s['variant_id'] as string } : {}),
    ...(line_items_evidence !== undefined && line_items_evidence.length > 0
      ? { line_items_evidence }
      : {}),
  };
}

/**
 * Parse + sanitize the multi-SKU `line_items` array (ADR-0077). Mirrors
 * the server projection: keep only entries that are objects with a
 * non-empty string `sku`; quantity defaults to 1 when absent/invalid
 * (non-positive or non-integer). Returns undefined when the input is not a
 * non-empty array or yields no valid entries, so the single-SKU back-comp
 * path (scalar `sku`) is preserved.
 */
function parseLineItems(
  raw: unknown,
): readonly { readonly sku: string; readonly quantity: number }[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: { sku: string; quantity: number }[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const sku = e['sku'];
    if (typeof sku !== 'string' || sku.length === 0) continue;
    const q = e['quantity'];
    const quantity = typeof q === 'number' && Number.isInteger(q) && q > 0 ? q : 1;
    out.push({ sku, quantity });
  }
  return out.length > 0 ? out : undefined;
}

function parseProductTitles(raw: unknown): Readonly<Record<string, string>> | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [sku, title] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof title === 'string' && title.length > 0) out[sku] = title;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Escalation service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
